import { ApolloLink } from "@apollo/client";
import { tap } from "rxjs";

/** GraphQL extension code when the option id is gone (deleted or unknown). */
export const DESIGN_OPTION_NOT_FOUND = "DESIGN_OPTION_NOT_FOUND";

export const STALE_DESIGN_OPTION_EVENT = "tf-design-option-not-found";

const NOTICE_WINDOW_MS = 1000;

type GraphQLErrorLike = {
  extensions?: { code?: string | undefined } | undefined;
};

let suppressUntil = 0;
let suppressedId: string | null = null;
let queued: { optionId: string | null } | null = null;

export const hasDesignOptionNotFound = (
  errors: readonly GraphQLErrorLike[] | undefined | null
): boolean =>
  Boolean(
    errors?.some((error) => error?.extensions?.code === DESIGN_OPTION_NOT_FOUND)
  );

/**
 * One notice per burst. Org-chart compare fires several queries at once;
 * each would otherwise toast.
 */
export const noteStaleDesignOption = (optionId?: string | null): void => {
  const id = optionId || null;
  const now = Date.now();
  if (now < suppressUntil && id === suppressedId) {
    return;
  }
  suppressUntil = now + NOTICE_WINDOW_MS;
  suppressedId = id;
  queued = { optionId: id };
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(STALE_DESIGN_OPTION_EVENT, { detail: { optionId: id } })
  );
};

/** The missed id, once, for a listener that was not mounted when the error arrived. */
export const takeQueuedStaleDesignOption = (): {
  optionId: string | null;
} | null => {
  const pending = queued;
  queued = null;
  return pending;
};

export const staleDesignOptionLink = new ApolloLink((operation, forward) =>
  forward(operation).pipe(
    tap({
      next: (result) => {
        if (hasDesignOptionNotFound(result.errors)) {
          const optionId = (
            operation.variables as { optionId?: string | null } | undefined
          )?.optionId;
          noteStaleDesignOption(optionId);
        }
      },
    })
  )
);
